// GroupedContactsSublist — brief 84 band B, candidate B24.
// Two primitives, not one: the clients and opponents halves differ in property
// accesses (p.clientId vs p.opponentId, g.client vs g.opponent) and i18n keys,
// which no free-name prop can carry — parameterising them would widen the
// primitive to swallow a hunk (move-integrity-standard 1b). Their mutual
// duplication stays, in one file, where the census keeps reporting it.
// Bodies are byte-identical to their CaseView donors; the CollectiveCaseView
// copies diffed empty against those donors modulo the renames the props carry
// (cls=committeeClients, cps=cps2 / ops=ops2, ocs=ocs2) plus prettier line
// wraps with identical token streams.
/* 214 — `import React`: react/react-in-jsx-scope is counted per JSX element and this is the rule's own remedy
   (189 §E's method). */
import React from "react";
import { FS } from "../lib/tokens";
// 214 §2–§4 [RULED 51–53] — a client's own counsel on the contacts' lines, and the counsel-by-party sublist.
import { counselOfParty, isClientCounsel, isSuspended, linkOf, partyIdsOf, shortDate, suspendedDateOf, type CounselLink, type LinkMap, type LinkState } from "../lib/counselLinks";
import type { TFn } from "../lib/taxonomyTypes";

export function ClientContactsSublist({ cls, cps, t, pname, links, roleLabel }: any) {
                                const grp = cls.map((c: any) => ({
                                  client: c,
                                  contacts: cps.filter(
                                    (p: any) => p.clientId === c.id,
                                  ),
                                }));
                                const orphans = cps.filter(
                                  (p: any) =>
                                    !p.clientId ||
                                    !cls.find((c: any) => c.id === p.clientId),
                                );
                                /* 214 §4 [RULED 53] — a contact with the role Avocat is the client's own counsel:
                                   its line carries the role's word and the link's state (`cpLinks`, a regular
                                   case; none in a collective case — acting). Typed at the seam: this file's
                                   `any` count may not rise. */
                                const tD = t as TFn;
                                const roleWord = typeof roleLabel === "string" && roleLabel ? roleLabel : tD("case.parties.opp_counsel", { fallback: "Attorney" });
                                const stateOf = (p: { id?: unknown }) => linkOf(links as LinkMap<LinkState>, Number(p.id));
                                const counselTag = (p: { id?: unknown; role?: unknown }) => {
                                  if (!isClientCounsel(p)) return null;
                                  const lk = stateOf(p);
                                  const since = isSuspended(lk)
                                    ? ` — ${tD("link.suspended_since", { date: shortDate(suspendedDateOf(lk)), fallback: `suspended since ${shortDate(suspendedDateOf(lk))}` })}`
                                    : "";
                                  return (
                                    <span data-contact-role="lawyer" style={{ color: "var(--tm)", fontSize: FS.s9_5, fontStyle: since ? "italic" : "normal" }}>
                                      {" "}· {roleWord}{since}
                                    </span>
                                  );
                                };
                                return (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      paddingLeft: 14,
                                      fontSize: FS.s10_5,
                                      lineHeight: 1.6,
                                      borderLeft: "1px solid var(--br)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: FS.s9,
                                        color: "var(--tm)",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {t("cc.parties.grouped_by_client", {
                                        fallback: "Grouped by Client",
                                      })}
                                    </div>
                                    {grp.map((g: any) => (
                                      <div
                                        key={g.client.id}
                                        style={{ marginBottom: 3 }}
                                      >
                                        <div
                                          style={{
                                            color: "var(--tp)",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {pname(g.client)}
                                        </div>
                                        {g.contacts.length === 0 ? (
                                          <div
                                            style={{
                                              color: "var(--tm)",
                                              paddingLeft: 14,
                                              fontStyle: "italic",
                                              fontSize: FS.s10,
                                            }}
                                          >
                                            →{" "}
                                            {t("cc.parties.no_contact_persons", {
                                              fallback: "No contact persons",
                                            })}
                                          </div>
                                        ) : (
                                          g.contacts.map((p: any) => (
                                            <div
                                              key={p.id}
                                              style={{
                                                paddingLeft: 14,
                                                color: "var(--ts)",
                                              }}
                                            >
                                              → {pname(p)}
                                              {p.function && (
                                                <span
                                                  style={{
                                                    color: "var(--tm)",
                                                    fontSize: FS.s9_5,
                                                  }}
                                                >
                                                  {" "}
                                                  · {p.function}
                                                </span>
                                              )}
                                              {counselTag(p)}
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    ))}
                                    {orphans.length > 0 && (
                                      <div
                                        style={{
                                          marginTop: 4,
                                          paddingTop: 4,
                                          borderTop: "1px dashed var(--br)",
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: "var(--tm)",
                                            fontSize: FS.s9,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.4,
                                          }}
                                        >
                                          {t("cc.parties.unlinked", {
                                            fallback: "Unlinked",
                                          })}
                                        </div>
                                        {orphans.map((p: any) => (
                                          <div
                                            key={p.id}
                                            style={{
                                              paddingLeft: 14,
                                              color: "var(--ts)",
                                            }}
                                          >
                                            → {pname(p)}
                                            {p.function && (
                                              <span
                                                style={{
                                                  color: "var(--tm)",
                                                  fontSize: FS.s9_5,
                                                }}
                                              >
                                                {" "}
                                                · {p.function}
                                              </span>
                                            )}
                                            {counselTag(p)}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
}

export function OpponentContactsSublist({ ops, ocs, t, pname }: any) {
                                const grp = ops.map((o: any) => ({
                                  opponent: o,
                                  contacts: ocs.filter(
                                    (c: any) => c.opponentId === o.id,
                                  ),
                                }));
                                const orphans = ocs.filter(
                                  (c: any) =>
                                    !c.opponentId ||
                                    !ops.find((o: any) => o.id === c.opponentId),
                                );
                                return (
                                  <div
                                    style={{
                                      marginTop: 6,
                                      paddingLeft: 14,
                                      fontSize: FS.s10_5,
                                      lineHeight: 1.6,
                                      borderLeft: "1px solid var(--br)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: FS.s9,
                                        color: "var(--tm)",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.4,
                                        marginBottom: 3,
                                      }}
                                    >
                                      {t("cc.parties.grouped_by_opponent", {
                                        fallback: "Grouped by Opponent",
                                      })}
                                    </div>
                                    {grp.map((g: any) => (
                                      <div
                                        key={g.opponent.id}
                                        style={{ marginBottom: 3 }}
                                      >
                                        <div
                                          style={{
                                            color: "var(--tp)",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {pname(g.opponent)}
                                        </div>
                                        {g.contacts.length === 0 ? (
                                          <div
                                            style={{
                                              color: "var(--tm)",
                                              paddingLeft: 14,
                                              fontStyle: "italic",
                                              fontSize: FS.s10,
                                            }}
                                          >
                                            →{" "}
                                            {t("cc.parties.no_contact_persons", {
                                              fallback: "No contact persons",
                                            })}
                                          </div>
                                        ) : (
                                          g.contacts.map((p: any) => (
                                            <div
                                              key={p.id}
                                              style={{
                                                paddingLeft: 14,
                                                color: "var(--ts)",
                                              }}
                                            >
                                              → {pname(p)}
                                              {p.function && (
                                                <span
                                                  style={{
                                                    color: "var(--tm)",
                                                    fontSize: FS.s9_5,
                                                  }}
                                                >
                                                  {" "}
                                                  · {p.function}
                                                </span>
                                              )}
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    ))}
                                    {orphans.length > 0 && (
                                      <div
                                        style={{
                                          marginTop: 4,
                                          paddingTop: 4,
                                          borderTop: "1px dashed var(--br)",
                                        }}
                                      >
                                        <div
                                          style={{
                                            color: "var(--tm)",
                                            fontSize: FS.s9,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.4,
                                          }}
                                        >
                                          {t("cc.parties.unlinked", {
                                            fallback: "Unlinked",
                                          })}
                                        </div>
                                        {orphans.map((p: any) => (
                                          <div
                                            key={p.id}
                                            style={{
                                              paddingLeft: 14,
                                              color: "var(--ts)",
                                            }}
                                          >
                                            → {pname(p)}
                                            {p.function && (
                                              <span
                                                style={{
                                                  color: "var(--tm)",
                                                  fontSize: FS.s9_5,
                                                }}
                                              >
                                                {" "}
                                                · {p.function}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
}

/* 214 §2 [RULED 51] — UNDER EACH OPPOSING PARTY, ITS COUNSEL'S NAMES, the way contacts sit under theirs: grouped
   by opponent through the LINK's parties (`ocuLinks` on a regular case; a collective case's elements through
   `ccLinksOf`, the same map shape), the state beside a suspended one; a counsel linked before 214 (no party yet)
   stays in the counsel list above and is listed here under « Non liés » until a party is ticked. Typed: this
   file's `any` count may not rise. A third primitive beside the two above, for the same reason they are two. */
interface PartyLike {
  id: number;
}
interface CounselLike {
  id: number;
}
type CounselSublistProps = {
  ops: readonly PartyLike[];
  ols: readonly CounselLike[];
  links: LinkMap<CounselLink>;
  t: TFn;
  pname: (x: unknown) => string;
};
export function OpponentCounselSublist({ ops, ols, links, t, pname }: CounselSublistProps) {
  const ids = ols.map((l) => l.id);
  const line = (l: CounselLike) => {
    const lk = linkOf(links, l.id);
    const since = isSuspended(lk)
      ? ` — ${t("link.suspended_since", { date: shortDate(suspendedDateOf(lk)), fallback: `suspended since ${shortDate(suspendedDateOf(lk))}` })}`
      : "";
    return (
      <div key={l.id} data-counsel-under-party={String(l.id)} style={{ paddingLeft: 14, color: since ? "var(--tm)" : "var(--ts)", fontStyle: since ? "italic" : "normal" }}>
        → {pname(l)}
        {since}
      </div>
    );
  };
  const unlinked = ols.filter((l) => partyIdsOf(linkOf(links, l.id)).length === 0);
  return (
    <div data-counsel-by-party="" style={{ marginTop: 6, paddingLeft: 14, fontSize: FS.s10_5, lineHeight: 1.6, borderLeft: "1px solid var(--br)" }}>
      <div style={{ fontSize: FS.s9, color: "var(--tm)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 3 }}>
        {t("cc.parties.grouped_counsel", { fallback: "Counsel by opposing party" })}
      </div>
      {ops.map((o) => {
        const mine = counselOfParty(ids, links, o.id)
          .map((id) => ols.find((l) => l.id === id))
          .filter((l): l is CounselLike => !!l);
        return (
          <div key={o.id} data-party-counsel={String(o.id)} style={{ marginBottom: 3 }}>
            <div style={{ color: "var(--tp)", fontWeight: 500 }}>{pname(o)}</div>
            {mine.length === 0 ? (
              <div style={{ color: "var(--tm)", paddingLeft: 14, fontStyle: "italic", fontSize: FS.s10 }}>
                → {t("counsel.under_party.none", { fallback: "No counsel" })}
              </div>
            ) : (
              mine.map(line)
            )}
          </div>
        );
      })}
      {unlinked.length > 0 && (
        <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed var(--br)" }}>
          <div style={{ color: "var(--tm)", fontSize: FS.s9, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {t("cc.parties.unlinked", { fallback: "Unlinked" })}
          </div>
          {unlinked.map(line)}
        </div>
      )}
    </div>
  );
}
